import { useEffect, useState } from 'react';
import axios from 'axios';
import { XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAID_TIERS = ['silver', 'gold', 'platinum'];

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

/**
 * Cancelling the coaching-program membership. This is NOT deleting the account: the account,
 * the marketplace, the forum and everything already bought all stay. The member simply drops
 * to Bronze.
 */
export const CancelMembershipCard = () => {
  const [plan, setPlan] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = () => {
    axios.get(`${API}/membership/my-plan`, auth())
      .then((r) => setPlan(r.data))
      .catch(() => {});
  };

  useEffect(load, []);

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/membership/cancel`, { confirm: true }, auth());
      setConfirming(false);
      toast.success('Your program membership has been cancelled. Your account stays with us.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  if (!plan) return null;

  const paid = PAID_TIERS.includes(plan.tier) && !plan.is_comped;
  const refund = plan.refund?.eligible
    ? money((plan.refund.refund_base_cents || 0) / 100)
    : null;

  // Nothing to cancel: Bronze never paid, and a comped or admin account was never billed.
  // The card still shows so the option is visibly there and says why it is unavailable.
  if (!paid) {
    return (
      <CollapsibleCard
        title="Cancel my program membership"
        icon={XCircle}
        testId="settings-cancel-membership"
        description="Leave the coaching program, keep your account"
      >
        <CardContent>
          <div className="text-sm text-gray-600 space-y-2" data-testid="cancel-membership-explainer">
            <p>
              {plan.is_comped
                ? 'Your access is comped by platform administration — there is no payment to cancel. If you want the comp removed, ask an administrator.'
                : 'You are on Bronze, the free tier. There is no paid program membership to cancel.'}
            </p>
            <p>
              Cancelling a program membership is a separate thing from closing your account. If
              you want to leave altogether, use <em>Delete my account</em> below.
            </p>
          </div>
        </CardContent>
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Cancel my program membership"
      icon={XCircle}
      testId="settings-cancel-membership"
      description="Leave the coaching program, keep your account"
    >
      <CardContent className="space-y-4">
        <div className="text-sm text-gray-600 space-y-2">
          <p data-testid="cancel-membership-explainer">
            This cancels your <strong>{plan.tier}</strong> coaching-program membership only.
            Your account stays open — the marketplace, the community forum, your orders, your
            bookings and anything you have already bought are all untouched. You drop to{' '}
            <strong>Bronze</strong>, which keeps Module 1 and the free areas of the platform.
          </p>
          <p>
            Program access ends straight away and any remaining plan payments are never owed.{' '}
            {refund
              ? `You are still inside the 7-day window, so we will refund ${refund} — the base you paid, not the fees or tax.`
              : 'The 7-day refund window has passed, so no refund applies.'}
          </p>
          <p>
            Your {money(plan.credit_toward_upgrade)} upgrade credit is forfeited, and coming back
            later starts fresh at full price. There is no waiting period to return.
          </p>
          <p className="text-gray-500">
            Want to close the account entirely instead? Use <em>Delete my account</em> below —
            cancel this first so you are not charged again.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => setConfirming(true)}
          data-testid="cancel-membership-btn"
        >
          <XCircle className="w-4 h-4 mr-2" /> Cancel my program membership
        </Button>

        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent data-testid="cancel-membership-dialog">
            <DialogHeader>
              <DialogTitle>Cancel your program membership?</DialogTitle>
              <DialogDescription>
                Program access ends right away and your {money(plan.credit_toward_upgrade)} credit
                is forfeited.{' '}
                {refund ? `We will refund ${refund}.` : 'No refund applies at this point.'}{' '}
                Your account itself is not deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirming(false)}>
                Keep my membership
              </Button>
              <Button
                onClick={cancel}
                disabled={busy}
                data-testid="cancel-membership-confirm-btn"
              >
                Yes, cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </CollapsibleCard>
  );
};

export default CancelMembershipCard;
